import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject, NgZone } from '@angular/core';
import { AppConfig } from '../app-config';
import { APP_CONFIG } from '../app-config.token';
import { TalentinaAskResponse } from '../contracts/responses/talentina-ask-response';
import { SignalRService } from './signalr.service';
import { BehaviorSubject, Observable, Subject, filter, first, scan, switchMap, timeout } from 'rxjs';

@Injectable()
export class TalentinaService {
  private http = inject(HttpClient);
  private config = inject<AppConfig>(APP_CONFIG);
  private signalRService = inject(SignalRService);
  private zone = inject(NgZone);

  private readonly HUB_NAME = 'ChatHub';
  private readonly API_URL = this.config.resourceApiURI + '/talentina';
  private subject = new Subject<string>();
  private resetSubject = new Subject<void>();
  private readonly connectedSubject = new BehaviorSubject<boolean>(false);

  readonly connected$ = this.connectedSubject.asObservable();

  public talentinaAnswer: Observable<string> = this.resetSubject.pipe(
    switchMap(() => this.subject.pipe(
      scan((acc, curr) => acc += curr, '')
    ))
  );


  constructor() {
    window.addEventListener('offline', () => {
      this.zone.run(() => this.connectedSubject.next(false));
    });

    window.addEventListener('online', () => {
      this.zone.run(() => this.reconnect());
    });
  }

  isConnected(): boolean {
    return this.signalRService.isConnected(this.HUB_NAME);
  }

  disconnect(): void {
    this.signalRService.closeConnection(this.HUB_NAME);
    this.connectedSubject.next(false);
  }

  connect(): void {
    this.signalRService.startConnection(this.HUB_NAME, undefined, {
      onclose: () => this.connectedSubject.next(false)
    }).pipe(switchMap(() => {
      this.connectedSubject.next(true);
      return this.signalRService.receiveMessage();
    })).subscribe((message) => {
      this.subject.next(message);
    });
  }

  reconnect(): void {
    this.signalRService.closeConnection(this.HUB_NAME);
    this.connect();
  }

  ask(question: string, thread: string): Observable<TalentinaAskResponse> {
    this.resetSubject.next();
    this.subject.next('');
    const url = `${this.API_URL}/ask`;
    return this.connected$.pipe(
      filter(Boolean),
      first(),
      timeout(15000),
      switchMap(() => {
        const connectionId = this.signalRService.getConnectionId(this.HUB_NAME);
        return this.http.post<TalentinaAskResponse>(url, { question, thread, connectionId });
      })
    );
  }

  cancel(): Observable<void> {
    const url = `${this.API_URL}/cancel`;
    const connectionId = this.signalRService.getConnectionId(this.HUB_NAME);

    return this.http.post<void>(url, { connectionId });
  }

  /**
   * Resolves whether the current user is currently eligible to use Talentina.
   * For agency workers (`TalentinaEmployee`) eligibility depends on active employment,
   * so the answer must be fetched from the backend rather than read from the JWT claims.
   */
  hasAccess(): Observable<boolean> {
    const url = `${this.API_URL}/hasAccess`;
    return this.http.get<boolean>(url);
  }
}